import type { CalendarDate } from '~/types/dates'
import type { SingletonKey } from '~/types/ids'
import { SINGLETON_KEY } from '~/types/ids'
import type { Centimetres } from '~/types/units'
import { issue, type RecordEnvelope, type ValidationIssues } from './common'
import { isWeekday, WEEKDAY_LABELS, type Weekday } from './weekday'

/** Display-only unit preferences. Storage stays canonical regardless (D-017). */
export type WeightDisplayUnit = 'kg' | 'lb'
export type LengthDisplayUnit = 'cm' | 'in'

/**
 * 0 = Sunday … 6 = Saturday, matching `Date.prototype.getDay`.
 *
 * An alias of `Weekday` rather than a second numbering of the same seven days
 * (D-105). A scheduled session names a weekday too, and two vocabularies for
 * one concept is how a schedule ends up a day out from the week it is
 * materialised into.
 */
export type WeekStartDay = Weekday

/**
 * A week-start change waiting for the current week to end.
 *
 * `effectiveFrom` is the first day the new value applies to — the start of the
 * week *after* the one running when the choice was made, computed from the day
 * in force at that moment rather than from the new one.
 */
export interface PendingWeekStartDay {
  readonly value: WeekStartDay
  readonly effectiveFrom: CalendarDate
}

/**
 * User preferences — a singleton.
 *
 * `lastBackupAt` is deliberately NOT here. It is operational state owned by the
 * `meta` store so it survives a settings reset (D-046).
 */
export interface SettingsRecord extends RecordEnvelope {
  readonly id: SingletonKey
  readonly weightDisplayUnit: WeightDisplayUnit
  readonly lengthDisplayUnit: LengthDisplayUnit
  /**
   * Calendar-week start for trends and export scope (D-034). Monday by default.
   *
   * **This is the day in force, not the day most recently chosen** (D-105 §8).
   * A change the user has made but which has not yet taken effect lives in
   * `pendingWeekStartDay` and moves here at the boundary.
   */
  readonly weekStartDay: WeekStartDay
  /**
   * A chosen week start that has not taken effect yet (D-105 §8).
   *
   * **A materialised week is authoritative for its entire inclusive span**, so
   * changing the week start cannot apply immediately: it would alter, truncate,
   * overlap or reinterpret a week already in progress. The new value therefore
   * waits for the current week to end.
   *
   * **Only ever one.** Choosing again before it takes effect *replaces* this
   * value; several queued future transitions are never appended, because a
   * user who changed their mind twice does not expect their weeks to shuffle
   * twice.
   *
   * Absent is the normal case, and every settings record written before C7.5
   * has it absent — which is why this needs no migration and why the
   * immediate-effect behaviour it replaces resolves identically.
   */
  readonly pendingWeekStartDay?: PendingWeekStartDay
  /** Configurable circumference spread warning threshold (D-016). */
  readonly circumferenceSpreadThresholdCm: Centimetres
}

/** Default 0.7 cm maximum spread before a retake warning (D-016). */
export const DEFAULT_SPREAD_THRESHOLD_CM = 0.7 as Centimetres

/** Monday, matching ISO weeks (D-034). */
export const DEFAULT_WEEK_START_DAY: WeekStartDay = 1

export const SETTINGS_KEY: SingletonKey = SINGLETON_KEY

export const WEIGHT_DISPLAY_UNITS: readonly WeightDisplayUnit[] = ['kg', 'lb']
export const LENGTH_DISPLAY_UNITS: readonly LengthDisplayUnit[] = ['cm', 'in']

/* One table, reached by two names. The setting's screen asks a different
   question from a session's weekday, but they render the same seven words. */
export const WEEK_START_DAY_LABELS: Readonly<Record<WeekStartDay, string>> = WEEKDAY_LABELS

export function isWeekStartDay(value: number): value is WeekStartDay {
  return isWeekday(value)
}

/**
 * Sanity bounds for the spread threshold — an entry-error guard, not coaching
 * policy. D-016 fixes the 0.7 cm default and requires the value stay
 * configurable, so no narrower opinion is imposed here; a zero or negative
 * threshold would simply break the warning it controls.
 */
const MIN_SPREAD_THRESHOLD_CM = 0.1
const MAX_SPREAD_THRESHOLD_CM = 10

export interface DisplayPreferencesInput {
  readonly weightDisplayUnit: string
  readonly lengthDisplayUnit: string
  readonly weekStartDay: number
  readonly circumferenceSpreadThresholdCm: number
}

export function validateDisplayPreferences(input: DisplayPreferencesInput): ValidationIssues {
  const issues: ValidationIssue[] = []

  if (!(WEIGHT_DISPLAY_UNITS as readonly string[]).includes(input.weightDisplayUnit)) {
    issues.push(issue('weightDisplayUnit', 'Choose either kilograms or pounds.'))
  }
  if (!(LENGTH_DISPLAY_UNITS as readonly string[]).includes(input.lengthDisplayUnit)) {
    issues.push(issue('lengthDisplayUnit', 'Choose either centimetres or inches.'))
  }
  if (!isWeekStartDay(input.weekStartDay)) {
    issues.push(issue('weekStartDay', 'Choose a day of the week for weeks to start on.'))
  }

  const threshold = input.circumferenceSpreadThresholdCm
  if (
    !Number.isFinite(threshold) ||
    threshold < MIN_SPREAD_THRESHOLD_CM ||
    threshold > MAX_SPREAD_THRESHOLD_CM
  ) {
    issues.push(
      issue(
        'circumferenceSpreadThresholdCm',
        `The measurement spread warning must be between ${MIN_SPREAD_THRESHOLD_CM} and ${MAX_SPREAD_THRESHOLD_CM} cm.`,
      ),
    )
  }

  return issues
}

type ValidationIssue = ValidationIssues[number]

export function defaultSettings(now: RecordEnvelope): SettingsRecord {
  return {
    id: SETTINGS_KEY,
    weightDisplayUnit: 'kg',
    lengthDisplayUnit: 'cm',
    weekStartDay: DEFAULT_WEEK_START_DAY,
    circumferenceSpreadThresholdCm: DEFAULT_SPREAD_THRESHOLD_CM,
    ...now,
  }
}
